<template>
    <rkm-form
        :show-legend="!data.model.exportado"
        ref="Form"
    >
        <rkm-row>
            <rkm-col cols="6">
                <rkm-form-input-autocomplete
                    :disabled="data.model.exportado || !!model.id"
                    :repository="repositories.usuario"
                    :search-fields="fields.autocomplete.usuario"
                    :validations="validations.usuario"
                    label="Usuário"
                    text-field="cidadaoNome"
                    v-model="model.usuario"
                />
            </rkm-col>

            <rkm-col cols="6">
                <rkm-form-input-autocomplete
                    :disabled="data.model.exportado || !!model.id || !model.usuario"
                    :repository="repositories.ocupacao"
                    :request-query-string-params="{ usuario_id: getSafely(() => model.usuario.id) }"
                    :search-fields="fields.autocomplete.ocupacao"
                    :text-field="o => `${o.codigo} - ${o.nome}`"
                    :validations="validations.ocupacao"
                    label="CBO"
                    v-model="model.ocupacao"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="7">
                <rkm-form-input-autocomplete
                    :disabled="data.model.exportado || !!model.id || !model.ocupacao"
                    :repository="repositories.procedimentoSigtap"
                    :request-query-string-params="{
                        instrumento_registro: enums.registro.RAAS_ATENCAO_PSICOSSOCIAL,
                        competencia_id: data.model.competenciaId,
                        ocupacao_id: getSafely(() => model.ocupacao.id),
                        paciente_id: data.model.paciente.id,
                    }"
                    :search-fields="fields.autocomplete.procedimento"
                    :text-field="p => `${formatProcedimento(p.codigo)} - ${p.nome}`"
                    :validations="validations.procedimento"
                    label="Procedimento"
                    v-model="model.procedimento"
                />
            </rkm-col>

            <rkm-col cols="2">
                <rkm-form-input
                    :disabled="data.model.exportado || !model.procedimento"
                    :validations="validations.quantidade"
                    label="Quantidade"
                    number
                    v-model="model.quantidade"
                />
            </rkm-col>

            <rkm-col cols="3">
                <rkm-form-date-picker
                    :disabled="data.model.exportado"
                    :min-date="minDateExecucao"
                    :max-date="maxDateExecucao"
                    :validations="validations.dataExecucao"
                    label="Data de execução"
                    right
                    v-model="model.dataExecucao"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="6">
                <rkm-form-input-autocomplete
                    :disabled="data.model.exportado || !model.procedimento"
                    :repository="repositories.servico"
                    :request-query-string-params="{ procedimento_id: getSafely(() => model.procedimento.id) }"
                    :search-fields="fields.autocomplete.servico"
                    :validations="validations.servico"
                    label="Serviço"
                    text-field="nome"
                    v-model="model.servico"
                />
            </rkm-col>

            <rkm-col cols="6">
                <rkm-form-input-autocomplete
                    :disabled="data.model.exportado || !model.servico"
                    :repository="repositories.classificacao"
                    :request-query-string-params="{
                        procedimento_id: getSafely(() => model.procedimento.id),
                        servico_id: getSafely(() => model.servico.id),
                    }"
                    :search-fields="fields.autocomplete.classificacao"
                    :validations="validations.classificacao"
                    label="Classificação"
                    text-field="nome"
                    v-model="model.classificacao"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-form-select
                    :disabled="data.model.exportado"
                    :options="data.form.procedimento.locaisRealizacaoExame"
                    :validations="validations.localRealizacaoExame"
                    label="Local de realização do exame"
                    show-default-option
                    text-field="nome"
                    v-model="model.localRealizacaoExameId"
                    value-field="id"
                />
            </rkm-col>
        </rkm-row>
    </rkm-form>
</template>

<script>
// Mixins.
import { repository, object, formats } from '@/mixins'

// Moment.
import moment from 'moment'

// Enums.
import registro from '@/enums/sigtap/registro'

export default {
    computed: {
        fields() {

            return {
                autocomplete: {
                    usuario: [
                        { key: 'cadastroCidadao.nome', searchable: true },
                    ],
                    ocupacao: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    procedimento: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    servico: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    classificacao: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                }
            }
        },
        validations() {

            return {
                usuario: { required: true },
                ocupacao: { required: true },
                procedimento: { required: true },
                quantidade: {
                    required: true,
                    min_value: 1,
                    max_value: this.model.procedimento ? this.model.procedimento.quantidadeMaximaExecucao : null,
                },
                dataExecucao: { required: true },
                servico: {},
                classificacao: {},
                localRealizacaoExame: { required: true },
            }
        },
        minDateExecucao() {

            const date = this.data.model.dataAdmissao > this.competencia.inicio
                ? this.data.model.dataAdmissao
                : this.competencia.inicio

            return this.others.moment(date).format('DD/MM/YYYY')
        },
        maxDateExecucao() {

            const dataAtual = this.others.moment(this.data.additionals.dataAtual, 'DD/MM/YYYY')
            let date = this.data.model.dataConclusao || dataAtual

            date = date < this.competencia.fim ? date : this.competencia.fim

            return this.others.moment(date).format('DD/MM/YYYY')
        },
        competencia() {

            const competencia = this.data.form.competencias.find(c => c.id === this.data.model.competenciaId).valor
            const ano = parseInt(competencia.substring(0, 4))
            const mes = parseInt(competencia.substring(4, 6))

            return {
                inicio: this.others.moment([ano, mes - 1]).startOf('month').toDate(),
                fim: this.others.moment([ano, mes - 1]).endOf('month').toDate()
            }
        }
    },
    created() {

        this.repositories = {
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
            ocupacao: this.getConectaSusRepository('sigtap.ocupacao'),
            procedimentoSigtap: this.getConectaSusRepository('sigtap.procedimento'),
            servico: this.getConectaSusRepository('sigtap.servico'),
            classificacao: this.getConectaSusRepository('sigtap.classificacao'),
        }
    },
    data: () => ({
        model: {
            id: null,
            usuario: null,
            ocupacao: null,
            procedimento: null,
            quantidade: null,
            dataExecucao: null,
            servico: null,
            classificacao: null,
            localRealizacaoExameId: null,
            saved: false,
        },
        enums: { registro },
        others: { moment }
    }),
    methods: {
        load(item = null) {

            if (item) {

                this.model = this.clone(item)
            }

            this.addWatchers()
        },
        addWatchers() {

            this.$watch('model.usuario', () => {

                this.model.ocupacao = null
            })

            this.$watch('model.ocupacao', () => {

                this.model.procedimento = null
            })

            this.$watch('model.procedimento', () => {

                this.model.quantidade = null
                this.model.servico = null
            })

            this.$watch('model.servico', () => {

                this.model.classificacao = null
            })
        },
        add() {

            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    const model = this.clone(this.model)

                    if (model.id) {

                        this.$set(
                            this.data.model.procedimentos,
                            this.data.model.procedimentos.findIndex(p => p.id === model.id),
                            model
                        )
                    }
                    else {

                        model.id = (new Date()).getTime()

                        this.data.model.procedimentos.push(model)
                    }

                    this.$emit('save')
                })
        },
    },
    mixins: [repository, object, formats],
    props: {
        data: { type: Object, required: true },
    },
}
</script>